import { STORAGE_KEYS, SUPER_ADMIN_ROLE } from '@/settings'

/**
 * 认证与用户信息工具 —— 对齐旧 common.js 的 getToken / getUserId /
 * getUserName / getUserPhotoImg / isSuperAdmin / logout。
 *
 * 说明：旧工程使用 sessionStorage 存放 token 与用户信息。
 * 这里刻意保持 sessionStorage，以便灰度期新旧页面共存时登录态互认。
 */

export interface UserInfo {
  id?: string | number
  userName?: string
  trueName?: string
  photoId?: string
  mobile?: string
  email?: string
  [key: string]: any
}

export interface InfoMap {
  /** 角色值，'99' 为超级管理员 */
  userRoleValue?: string
  /** 按钮/菜单权限集合 */
  resourceList?: string[]
  [key: string]: any
}

/* ------------------------------- token ------------------------------- */

export function getToken(): string {
  return sessionStorage.getItem(STORAGE_KEYS.TOKEN) || ''
}

export function setToken(token: string): void {
  sessionStorage.setItem(STORAGE_KEYS.TOKEN, token)
}

/* ------------------------------ 用户信息 ------------------------------ */

export function getUserInfo(): UserInfo | null {
  const raw = sessionStorage.getItem(STORAGE_KEYS.USER_INFO)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function setUserInfo(user: UserInfo): void {
  sessionStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(user))
}

export function getUserId(): string | number | undefined {
  return getUserInfo()?.id
}

export function getUserName(): string {
  return getUserInfo()?.userName || ''
}

export function getUserTrueName(): string {
  const u = getUserInfo()
  return u?.trueName || u?.userName || ''
}

export function getUserPhotoId(): string {
  return getUserInfo()?.photoId || ''
}

/* ------------------------------ 权限信息 ------------------------------ */

export function getInfoMap(): InfoMap | null {
  const raw = sessionStorage.getItem(STORAGE_KEYS.INFO_MAP)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function setInfoMap(infoMap: InfoMap): void {
  sessionStorage.setItem(STORAGE_KEYS.INFO_MAP, JSON.stringify(infoMap))
}

/** 是否超级管理员（旧 isSuperAdmin） */
export function isSuperAdmin(): boolean {
  return getInfoMap()?.userRoleValue === SUPER_ADMIN_ROLE
}

/** 按钮级权限判断：超级管理员放行，其余查 resourceList */
export function hasPermission(code?: string | string[]): boolean {
  if (!code) return true
  if (isSuperAdmin()) return true
  const list = getInfoMap()?.resourceList || []
  const codes = Array.isArray(code) ? code : [code]
  return codes.some((c) => list.includes(c))
}

/* ------------------------------ 系统配置 ------------------------------ */

export function getSystemConfig(): Record<string, string> {
  return {
    domainName: sessionStorage.getItem(STORAGE_KEYS.DOMAIN_NAME) || '',
    copyRight: sessionStorage.getItem(STORAGE_KEYS.COPY_RIGHT) || '',
    officialAccounts: sessionStorage.getItem(STORAGE_KEYS.OFFICIAL_ACCOUNTS) || '',
    logo: sessionStorage.getItem(STORAGE_KEYS.LOGO) || ''
  }
}

export function setSystemConfig(cfg: Record<string, any>): void {
  if (!cfg) return
  if (cfg.domainName != null) sessionStorage.setItem(STORAGE_KEYS.DOMAIN_NAME, String(cfg.domainName))
  if (cfg.copyRight != null) sessionStorage.setItem(STORAGE_KEYS.COPY_RIGHT, String(cfg.copyRight))
  if (cfg.officialAccounts != null)
    sessionStorage.setItem(STORAGE_KEYS.OFFICIAL_ACCOUNTS, String(cfg.officialAccounts))
  if (cfg.logo != null) sessionStorage.setItem(STORAGE_KEYS.LOGO, String(cfg.logo))
}

/* ------------------------------- 清理 ------------------------------- */

/** 仅清理登录态相关，保留系统配置缓存 */
export function clearAuth(): void {
  sessionStorage.removeItem(STORAGE_KEYS.TOKEN)
  sessionStorage.removeItem(STORAGE_KEYS.USER_INFO)
  sessionStorage.removeItem(STORAGE_KEYS.INFO_MAP)
  sessionStorage.removeItem(STORAGE_KEYS.MENU)
  sessionStorage.removeItem(STORAGE_KEYS.MENU2)
}

/** 完全清理（旧 logout 的 sessionStorage.clear()） */
export function clearAll(): void {
  sessionStorage.clear()
}
